import { Capacitor } from '@capacitor/core';
import type { AppType } from '@/types';
import { BUILT_PRODUCT } from '@/product';

/**
 * Resolve the AppType for web/Telegram from the current URL.
 *
 * Two signals, in priority order:
 *  1. Hostname — GreekBrain has its own domain (app.greekbrain.app,
 *     app-stage.greekbrain.app), so the host alone identifies the app and the
 *     bare `/` can redirect straight into it.
 *  2. Pathname — the original scheme, where both apps were served from
 *     app.hallouminati.app and only `/:appType/...` told them apart. Still the
 *     authority for the Telegram mini-apps and any legacy link.
 *
 * Hallouminati is the fallback: it predates the split and owns the shared host.
 *
 * A bundle built with VITE_APP is that product regardless of URL — the host
 * it is served from was chosen by the deploy, not by this function.
 */
export function detectWebAppType(
    hostname: string = window.location.hostname,
    pathname: string = window.location.pathname,
): AppType {
    if (BUILT_PRODUCT) return BUILT_PRODUCT;
    if (hostname.includes('greekbrain')) return 'greekbrain';
    if (hostname.includes('hallouminati')) {
        return pathname.includes('greekbrain') ? 'greekbrain' : 'hallouminati';
    }
    // Unknown host (localhost, preview deploys, Telegram web) — path decides.
    return pathname.includes('greekbrain') ? 'greekbrain' : 'hallouminati';
}

/**
 * The AppType last resolved from the native bundle id, cached so the next launch
 * knows which app it is *synchronously*.
 *
 * The native builds share one web bundle and one Capacitor host
 * (`app.hallouminati.app`), so in a native app the URL says nothing about which
 * app is running — only `App.getInfo().id` does, and that is async. Anything
 * that must pick per app before the first await (Sentry's DSN) reads this cache.
 */
const NATIVE_APP_TYPE_KEY = 'native_app_type';

/** Remember the app type resolved from the native bundle id. */
export function rememberNativeAppType(appType: AppType): void {
    try {
        localStorage.setItem(NATIVE_APP_TYPE_KEY, appType);
    } catch {
        // Storage can be unavailable (private mode, quota) — the cache is an
        // optimisation, never a requirement.
    }
}

/**
 * The AppType if it can be determined *right now*, without awaiting anything.
 *
 * Returns null only on a native launch we have never resolved before (a fresh
 * install), where the bundle id is the sole signal and it is async. Callers
 * that get null should wait for `platformService.getAppType()`.
 */
export function resolveKnownAppType(): AppType | null {
    if (BUILT_PRODUCT) return BUILT_PRODUCT;
    if (!Capacitor.isNativePlatform()) {
        return detectWebAppType();
    }

    try {
        const cached = localStorage.getItem(NATIVE_APP_TYPE_KEY);
        if (cached === 'greekbrain' || cached === 'hallouminati') {
            return cached;
        }
    } catch {
        // See rememberNativeAppType().
    }

    return null;
}
